/**
 * AnalyticsLayout — Sidebar secundaria (desktop) / Tabs scrolleables (mobile)
 * para alternar entre Resumen y WhatsApp dentro de Analíticas.
 *
 * Analytics ya lee `?tab=` del URL para decidir qué panel mostrar; aquí solo
 * controlamos esa query param desde la navegación.
 */
import React from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, List, ListItemButton, ListItemIcon, ListItemText, Tabs, Tab,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import Analytics from '../Analytics';

const SECTIONS = [
  { tab: 'resumen',  label: 'Resumen',  icon: <AssessmentIcon />, color: '#164e63' },
  { tab: 'whatsapp', label: 'WhatsApp', icon: <WhatsAppIcon />,   color: '#25D366' },
];

export default function AnalyticsLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const currentTab = searchParams.get('tab') === 'whatsapp' ? 'whatsapp' : 'resumen';

  const activeIdx = Math.max(0, SECTIONS.findIndex(s => s.tab === currentTab));

  const setTab = (tab) => navigate(`/analytics?tab=${tab}`, { replace: true });

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', bgcolor: '#f8fafc' }}>
      {/* Sidebar secundario (desktop) */}
      <Box sx={{
        width: 240, flexShrink: 0, bgcolor: '#fff',
        borderRight: '1px solid #e2e8f0',
        display: { xs: 'none', md: 'flex' }, flexDirection: 'column',
      }}>
        <Box sx={{ p: 2.5, pb: 1.5 }}>
          <Typography sx={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>
            Analíticas
          </Typography>
        </Box>
        <List sx={{ px: 1, py: 0 }} dense>
          {SECTIONS.map(s => {
            const isActive = s.tab === currentTab;
            return (
              <ListItemButton key={s.tab} onClick={() => setTab(s.tab)}
                sx={{
                  borderRadius: 2, mb: 0.5, py: 0.75, pl: 1.5,
                  bgcolor: isActive ? `${s.color}15` : 'transparent',
                  borderLeft: isActive ? `3px solid ${s.color}` : '3px solid transparent',
                  '&:hover': { bgcolor: isActive ? `${s.color}25` : '#f1f5f9' },
                }}>
                <ListItemIcon sx={{ minWidth: 32, color: isActive ? s.color : '#94a3b8' }}>
                  {s.icon}
                </ListItemIcon>
                <ListItemText primary={s.label}
                  primaryTypographyProps={{
                    fontSize: 13, fontWeight: isActive ? 700 : 500,
                    color: isActive ? '#0f172a' : '#475569',
                  }} />
              </ListItemButton>
            );
          })}
        </List>

        <Box sx={{ flex: 1 }} />
        <Box sx={{ p: 2, borderTop: '1px solid #f1f5f9' }}>
          <Typography sx={{ fontSize: 10.5, color: '#94a3b8', lineHeight: 1.5 }}>
            Métricas operativas y del canal WhatsApp.
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {/* Tabs móviles */}
        <Box sx={{
          display: { xs: 'block', md: 'none' },
          borderBottom: '1px solid #e2e8f0',
          bgcolor: '#fff',
          position: 'sticky', top: 0, zIndex: 5,
        }}>
          <Tabs
            value={activeIdx}
            onChange={(_, idx) => setTab(SECTIONS[idx].tab)}
            variant="fullWidth"
            sx={{
              minHeight: 48,
              '& .MuiTab-root': { minHeight: 48, textTransform: 'none', fontWeight: 700, fontSize: 13 },
              '& .MuiTabs-indicator': { backgroundColor: SECTIONS[activeIdx]?.color || '#164e63', height: 3, borderRadius: 2 },
            }}
          >
            {SECTIONS.map(s => (
              <Tab key={s.tab} icon={s.icon} iconPosition="start" label={s.label} />
            ))}
          </Tabs>
        </Box>

        <Analytics />
      </Box>
    </Box>
  );
}
